/* eslint no-loop-func: 0 */
import {
  take,
  put,
  fork,
  cancel,
  select,
  cancelled,
  actionChannel,
  spawn,
  all,
} from "redux-saga/effects"

import devlog from "src/helpers/devlog"
import map from "lodash/map"
import omitBy from "lodash/omitBy"
import toString from "lodash/toString"
import union from "lodash/union"
import { request } from "src/engine/modules/session/effects"
import { normalize as normalizeModel } from "src/engine/modules/model/helpers"
import {
  process as selectProcess,
  observer as selectObserver,
  queries as selectQueries,
  rawData as selectRawData,
} from "../selectors"
import { types, creators } from "../actions"
import { normalize } from "../helpers"

function* startObserveModel(engine, currentModel) {
  while (true) {
    const { model } = yield take([
      engine.actions.types.removeSuccess,
      engine.actions.types.createSuccess,
    ])
    if (model.name === currentModel.name) {
      // Get all queries for this model and refresh it
      const queries = yield select(selectQueries, engine, currentModel)
      // Update all related collections
      yield all(
        map(queries, data => put(creators.fetch(engine, model, data.query))),
      )
    }
  }
}

/* Observe model to remove and create actions, to refresh list */
function* observeModel(engine, currentModel) {
  const observer = yield select(selectObserver, engine, currentModel)
  if (!observer) {
    // Start new observer
    const newObserver = yield fork(startObserveModel, engine, currentModel)
    yield put(creators.observer.set(engine, currentModel, newObserver))

    // Watch for observer unset
    yield take(types.unsetObserver)
    const observer = yield select(selectObserver, engine, currentModel)
    yield cancel(observer)
  }
}

/* eslint consistent-return: 0 */
/* No reason to return a value, also return makes fork unresolvable */
function* fetch(action) {
  const { engine, model, query } = action

  const cumulative = model.collection && model.collection.cumulative

  try {
    const { getUrl, getRestUrn } = normalizeModel(model)

    const requestURL = getRestUrn ? getRestUrn(`get`) : getUrl() // TODO: remove legacy function support

    const { data: nextData, response } = yield request(
      requestURL,
      query || {},
      {
        dedup: true,
      },
    )
    const { data, result } = normalize(nextData, model)
    if (data) {
      /* Write data from every collection item to the engine state
       * I use batch action to reduce redux lifecycle itertions count */
      yield put(
        engine.actions.creators.batch(
          model,
          map(data, (item, id) =>
            engine.actions.creators.read.success(model, id, item),
          ),
        ),
      )

      /* Fist release each item of the previous collection */
      const previousData = yield select(selectRawData, engine, model, query)
      const onlyNewData = omitBy(data, (data, id) =>
        previousData.includes(
          // #IdToString
          toString(id),
        ),
      )
      /* Filter last unused items */
      const onlyOldData = previousData.filter(id => !data[id])

      /* Release each old item in override mode */
      if (!cumulative) {
        yield put(
          engine.actions.creators.batch(
            model,
            map(onlyOldData, (data, id) =>
              engine.actions.creators.release(model, id),
            ),
          ),
        )
      }

      /* Reserve each new item */
      yield put(
        engine.actions.creators.batch(
          model,
          map(onlyNewData, (data, id) =>
            engine.actions.creators.involve(model, id),
          ),
        ),
      )

      /* Observe model item remove/create */
      yield spawn(observeModel, engine, model)

      const merge =
        model.collection && model.collection.mergeStrategy
          ? model.collection.mergeStrategy
          : union

      /* Get final data considering append parametr */
      const finalData = cumulative
        ? merge(result, previousData, action)
        : result

      /* Success fetch collection */
      yield put(
        creators.fetch.success(engine, model, query, finalData, {
          totalCount: response.headers.get(`Total-Count`),
        }),
      )
    } else {
      throw new Error(`No data provided`)
    }
  } catch (error) {
    devlog(error)
    yield put(creators.fetch.error(engine, model, query, error))
    yield error
  } finally {
    if (yield cancelled()) {
      yield put(creators.create.cancel(engine, model, query))
    }
  }
}

export default function* fetchSaga() {
  const fetchRequests = yield actionChannel([types.fetch])
  while (true) {
    const action = yield take(fetchRequests)
    const { engine, model, query } = action

    const process = yield select(selectProcess, engine, model, query)

    if (action.type === types.fetch) {
      if (process) {
        switch (process.type) {
          /* Eject if already fetched */
          case types.fetch: {
            break
          }
          default:
            throw new Error(`Unknown entity action type founded`)
        }
      }

      const task = yield fork(fetch, action)
      yield put(
        creators.processing.start(types.fetch, task, engine, model, query),
      )
      yield fork(function* watchFork() {
        yield task.toPromise()
        yield put(
          creators.processing.complete(types.fetch, task, engine, model, query),
        )
      })
      // If fetch completed, we should erase existen progress
    } else if (action.type === types.fetchCancel) {
      if (process) {
        const { task } = process
        if (process.type === types.create) {
          yield cancel(task)
        } else {
          throw new Error(
            `Unexpected cancellation of process with another task`,
          )
        }
      }
    }
  }
}
